/**
 * Banner shown above the action list when a pending proposal is in a
 * partial-failure retry state — some current-version actions already
 * committed successfully on a prior attempt, and at least one failed.
 *
 * Self-suppressing: returns null when `getPartialExecutionState` returns null
 * (i.e. proposal is not pending, or no actions have succeeded yet).
 *
 * Uses StatusBanner (warning variant) so it stays visually consistent with
 * other system-status banners on settings and proposal surfaces.
 *
 * The Approve button is intentionally left enabled — re-approval is safe
 * because the executor skips already-committed actions. The banner conveys
 * the distinction; blocking the button would strand the failed actions.
 * (StickyFooter.tsx already gates its `actionAlreadyExecuted` guard on
 * `!proposalIsPending`, so it does not block re-approval in this state.)
 */

import { AlertTriangle } from '@untitledui/icons';
import React, { useMemo } from 'react';

import { StatusBanner } from '@/lib/base/banners/StatusBanner';
import type { ProposalDetail } from '@/lib/proposals/core/types';
import { getAlreadyExecutedBadgeText } from '@/lib/proposals/utils/actionWarnings';
import { getPartialExecutionState } from '@/lib/proposals/utils/partialExecution';

interface PartialExecutionBannerProps {
  proposal: Pick<ProposalDetail, 'resolution' | 'actions'>;
}

export function PartialExecutionBanner({
  proposal,
}: PartialExecutionBannerProps) {
  const state = useMemo(() => getPartialExecutionState(proposal), [proposal]);

  if (state === null) {
    return null;
  }

  const { alreadyExecuted, willReExecute } = state;
  const count = alreadyExecuted.length;
  const reRunCount = willReExecute.length;

  const title =
    count === 1
      ? "1 action already completed – won't run again"
      : `${count} actions already completed – won't run again`;

  // Build a readable list of action labels (e.g. "Already sent, already
  // created"). Deduplicate so identical labels collapse to one entry.
  const uniqueLabels = [
    ...new Set(alreadyExecuted.map(a => getAlreadyExecutedBadgeText(a))),
  ]
    .map(l => l.toLowerCase())
    .join(', ');

  const capitalised =
    uniqueLabels.charAt(0).toUpperCase() + uniqueLabels.slice(1);

  // `reRunCount` covers every selected action re-approval will run — the failed
  // ones AND any selected actions never attempted on the prior run — so the copy
  // never implies only failures are retried.
  let description: string;
  if (reRunCount === 0) {
    description = `${capitalised} on a previous attempt. Re-approving will not re-run ${count === 1 ? 'it' : 'them'}.`;
  } else if (reRunCount === 1) {
    description = `${capitalised} on a previous attempt. Re-approving skips ${count === 1 ? 'it' : 'them'} and runs the 1 remaining selected action.`;
  } else {
    description = `${capitalised} on a previous attempt. Re-approving skips ${count === 1 ? 'it' : 'them'} and runs the ${reRunCount} remaining selected actions.`;
  }

  return (
    <StatusBanner
      variant='warning'
      icon={AlertTriangle}
      title={title}
      description={description}
      className='mx-3 my-2'
    />
  );
}
